import { ApolloClient, gql } from '@apollo/client';
import { create } from '@thoughtspot/logger';
import { isSpreadsheetsEnabled } from '@thoughtspot/session-service';
import { isEmbeddedApp } from '/@utils/embed-base.util';

const logger = create('saved-from-spreadsheet');

/**
 * Answer-level clientState marker recording which view (Answer vs Spreadsheet)
 * the answer was last saved from. Written by the spreadsheet store's save
 * slice (js/ts-apps/spreadsheet save-service) and cleared session-level by
 * SavedAnswerLayout when the Answer view becomes active — the clear only
 * persists if the user then saves, which is exactly when "last saved from"
 * flips to the Answer view. Read by SavedAnswerLayout to reopen the answer in
 * the view it was saved from. Answers saved before this marker existed carry
 * no entry and keep opening in the classic Answer view (backward compatible
 * by absence). The answer-page save flow itself is deliberately untouched.
 *
 * Must stay in sync with SAVED_FROM_SPREADSHEET_KEY in
 * js/ts-apps/spreadsheet/src/services/save/save-service.ts — importing it
 * from @thoughtspot/spreadsheet here would pull that package into blink-v2's
 * main chunk (this module is reached from the route layout).
 */
export const SAVED_FROM_SPREADSHEET_KEY = 'savedFromSpreadsheet';

export type AnswerClientState = Record<string, unknown> | null | undefined;

export const wasSavedFromSpreadsheet = (
    clientState: AnswerClientState,
): boolean => clientState?.[SAVED_FROM_SPREADSHEET_KEY] === true;

/**
 * Whether opening this saved answer should land on the /spreadsheet sibling
 * route. False whenever the spreadsheet feature is off or the app is embedded
 * (the embed routes have no spreadsheet child), so those contexts always keep
 * today's behavior and land on the Answer view.
 */
export const shouldReopenInSpreadsheet = (
    clientState: AnswerClientState,
): boolean =>
    wasSavedFromSpreadsheet(clientState) &&
    isSpreadsheetsEnabled() &&
    !isEmbeddedApp();

// Answer__updateAnswerClientState REPLACES the whole clientState blob, so the
// caller-provided current blob is merged before sending. The response is
// written to the cache on purpose: the fresh genNo keeps the follow-up
// Answer__save on the current generation, and the returned answer.clientState
// keeps the cached Answer entity in sync for later reads.
const UPDATE_ANSWER_CLIENT_STATE = gql`
    mutation ClearSavedFromSpreadsheet(
        $session: BachSessionIdInput!
        $clientState: JSON!
    ) {
        Answer__updateAnswerClientState(
            session: $session
            clientState: $clientState
        ) {
            id {
                sessionId
                genNo
            }
            answer {
                id
                clientState
            }
        }
    }
`;

const SESSION_GEN_NO_FRAGMENT = gql`
    fragment SavedFromSpreadsheetSessionGenNo on BachSessionId {
        genNo
    }
`;

const readSessionGenNo = (
    client: ApolloClient<object>,
    sessionId: string,
): number | undefined => {
    try {
        const id = client.cache.identify({
            __typename: 'BachSessionId',
            sessionId,
        });
        if (!id) {
            return undefined;
        }
        const fragment = client.readFragment<{ genNo?: number }>({
            id,
            fragment: SESSION_GEN_NO_FRAGMENT,
        });
        return typeof fragment?.genNo === 'number' ? fragment.genNo : undefined;
    } catch {
        return undefined;
    }
};

const ANSWER_CLIENT_STATE_FRAGMENT = gql`
    fragment SavedFromSpreadsheetAnswerClientState on Answer {
        clientState
    }
`;

/**
 * Live clientState of the cached Answer entity, or undefined when the entity
 * (or its clientState field) is not in the cache. Fresher than a load-time
 * snapshot: Answer__load normalizes clientState into the cache on every load,
 * the spreadsheet save slice mirrors its marker stamp here, and
 * clearSavedFromSpreadsheetFlag's mutation response writes back the cleared
 * blob — so this reflects marker changes made after a snapshot was taken.
 * Callers should fall back to their snapshot on undefined.
 */
export const readCachedAnswerClientState = (
    client: ApolloClient<object>,
    answerId: string,
): AnswerClientState => {
    try {
        const id = client.cache.identify({
            __typename: 'Answer',
            id: answerId,
        });
        if (!id) {
            return undefined;
        }
        const cached = client.readFragment<{
            clientState?: Record<string, unknown> | null;
        }>({
            id,
            fragment: ANSWER_CLIENT_STATE_FRAGMENT,
        });
        // readFragment returns null when the field is missing; null clientState
        // on a cached entity is a real value (marker absent), so map only the
        // missing-entity case to undefined.
        return cached === null ? undefined : cached.clientState;
    } catch {
        return undefined;
    }
};

/**
 * Drops the saved-from-spreadsheet marker from the SESSION clientState, so
 * that if the user saves from the Answer view the answer persists without the
 * marker and reopens in the Answer view. Session mutations are only persisted
 * by a subsequent Answer__save — merely viewing the Answer sibling never
 * rewrites the stored answer. No-ops unless the marker is currently set —
 * answers that never visited the spreadsheet (including every pre-marker
 * answer) cost no extra request. Best-effort: a failure here must never block
 * the caller.
 */
export const clearSavedFromSpreadsheetFlag = async (
    client: ApolloClient<object>,
    answerSessionId: string,
    clientState: AnswerClientState,
): Promise<void> => {
    if (!wasSavedFromSpreadsheet(clientState)) {
        return;
    }
    try {
        const genNo = readSessionGenNo(client, answerSessionId);
        if (genNo === undefined) {
            // Without a genNo the backend may reject the mutation for being
            // off-generation, leaving the marker set after a silently-failed
            // request. Skipping keeps the failure deterministic and preserves
            // the no-extra-request guarantee; in practice Answer__load has
            // always populated the cache before the user can save.
            logger.warn(
                'clearSavedFromSpreadsheetFlag: session genNo not in cache; skipping clear',
                answerSessionId,
            );
            return;
        }
        // Drop the key entirely (rather than writing false) so the marker's
        // "absent = opens in Answer view" semantics hold and the persisted
        // blob does not accumulate a permanent field.
        const { [SAVED_FROM_SPREADSHEET_KEY]: _dropped, ...rest } =
            clientState ?? {};
        await client.mutate({
            mutation: UPDATE_ANSWER_CLIENT_STATE,
            variables: {
                session: { sessionId: answerSessionId, genNo },
                clientState: rest,
            },
        });
    } catch (e) {
        logger.error('Failed to clear saved-from-spreadsheet marker', e);
    }
};
